import cn from "@/v2-ui/utils/utils.cn"
import type { BadgeVariant } from "@/v2-backoffice-client/components/badge"
import BadgeList from "@/v2-backoffice-client/components/badgeList"
import { packageSheetHref } from "@/v2-backoffice-client/license/changes/license.changes.constants"
import { useLicenseChanges } from "@/v2-backoffice-client/license/changes/license.changes.context"

export type LicensePackageBadgesProps = {
  /** The packages that carry the feature, by name, as the catalog lists them. */
  names: readonly string[]
  /** How the pills read: a package-only feature's packages are its access, a free one's a placement. */
  variant?: BadgeVariant
  /** What stands in the cell when no package carries the feature. */
  emptyLabel: string
  /** Whether that empty state is a problem (package-only with nobody to carry it). */
  isEmptyCritical?: boolean
  /**
   * Rendered inside an open panel: a click pushes the package onto its
   * trail instead of replacing it, and there is room to show every package.
   */
  isNested?: boolean
  tooltip?: string
  className?: string
}

// The packages that carry a feature, as pills that open the package panel
// (where the package's contents are composed). Names come from the catalog
// row; the ids come from the package vocabulary the board already holds, so
// a name the vocabulary no longer knows still reads, just without a link.
function LicensePackageBadges(props: LicensePackageBadgesProps) {
  const {
    names,
    variant = "neutral",
    emptyLabel,
    isEmptyCritical = false,
    isNested = false,
    tooltip,
    className,
  } = props
  const { packages } = useLicenseChanges()

  return (
    <BadgeList
      className={className}
      variant={variant}
      overflowTitle="Packages"
      max={isNested ? Infinity : undefined}
      isWrapped={isNested}
      items={names.map((name) => {
        const id = packages.find(
          (featureGroup) => featureGroup.Name === name,
        )?.Id
        return {
          label: name,
          to: id === undefined ? undefined : packageSheetHref(id, isNested),
          tooltip,
        }
      })}
      emptyLabel={
        <span
          className={cn(
            "text-xs",
            isEmptyCritical ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {emptyLabel}
        </span>
      }
    />
  )
}

export default LicensePackageBadges
